import { useState, useRef, useEffect } from 'react';
import { Button } from '@carbon/react';
import { Close, AddComment } from '@carbon/react/icons';
import { useAtom, useSetAtom } from 'jotai';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { CommentInput } from './comment-input';
import { ActivityFeedList } from './activity-feed-list';
import { isActivityFeedPanelOpenAtom, useActivityFeedAtom } from './atoms';
import type { ActivityFeedItem } from './types';
import style from './activity-feed-panel.module.scss';

interface ActivityFeedPanelProps {
  modelType: string;
  modelId?: number;
  title: string;
  subtitle?: string;
  commentsHref: string;
}

/**
 * Right-side drawer that displays the paginated activity feed for a record
 * (when `modelId` is supplied) or across all records of a type (when omitted).
 * Visibility is controlled via the shared `isActivityFeedPanelOpenAtom`. Pass
 * `commentsHref=""` to suppress the comment input.
 * @param {ActivityFeedPanelProps} props - Drawer configuration.
 * @return {JSX.Element} The drawer markup (rendered through Carbon's `Drawer`).
 */
export const ActivityFeedPanel = ({
  modelType,
  modelId,
  title,
  subtitle,
  commentsHref,
}: ActivityFeedPanelProps) => {
  const [open, setOpen] = useAtom(isActivityFeedPanelOpenAtom);
  const { searchAtom, resetSearchDataAtom } = useActivityFeedAtom(
    modelType,
    modelId
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const resetSearch = useSetAtom(resetSearchDataAtom);

  const [accumulatedItems, setAccumulatedItems] = useState<ActivityFeedItem[]>(
    []
  );
  const lastPageRef = useRef<number>(0);

  const totalCount = searchResult.data?.totalCount ?? 0;
  const currentPage = searchResult.data?.page ?? 1;
  const pageSize = searchResult.data?.pageSize ?? 25;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasMore = currentPage < totalPages;

  useEffect(() => {
    const currentResults = searchResult.data?.results ?? [];
    if (currentResults.length > 0 && currentPage !== lastPageRef.current) {
      if (currentPage === 1) {
        setAccumulatedItems(currentResults);
      } else {
        setAccumulatedItems((prev) => [...prev, ...currentResults]);
      }
      lastPageRef.current = currentPage;
    }
  }, [searchResult.data?.results, currentPage]);

  useEffect(() => {
    if (open) {
      setAccumulatedItems([]);
      lastPageRef.current = 0;
      resetSearch({ unsetCurrentPayload: true });
      setSearchPayload(() => ({
        modelType,
        modelId,
        page: 1,
        pageSize: 25,
        sortAttribute: 'created_at',
        sortDirection: 'DESC',
      }));
    }
  }, [open, modelType, modelId, resetSearch, setSearchPayload]);

  const items = accumulatedItems;

  const handleLoadMore = () => {
    if (searchResult.loading || !hasMore) return;

    setSearchPayload((current) => {
      if (!current) return undefined;
      return {
        ...current,
        page: (current.page ?? 1) + 1,
      };
    });
  };

  const handleCommentSuccess = () => {
    setAccumulatedItems([]);
    lastPageRef.current = 0;
    resetSearch({ unsetCurrentPayload: true });
    setSearchPayload(() => ({
      modelType,
      modelId,
      page: 1,
      pageSize: 25,
      sortAttribute: 'created_at',
      sortDirection: 'DESC',
    }));
  };

  return (
    <Drawer
      lockBackgroundScroll
      open={open}
      direction="right"
      size="100%"
      className={style.activityFeedDrawer}
      onClose={() => setOpen(false)}
    >
      {open && (
        <>
          <DrawerHeader>
            <div className={style.drawerHeader}>
              <div className={style.headerText}>
                {subtitle && <span className={style.subtitle}>{subtitle}</span>}
                <h2>{title}</h2>
              </div>
              <Button
                hasIconOnly
                size="lg"
                kind="ghost"
                onClick={() => setOpen(false)}
                iconDescription="Close"
                tooltipAlignment="center"
                tooltipPosition="bottom"
                renderIcon={(props) => <Close size={20} {...props} />}
              />
            </div>
          </DrawerHeader>
          <DrawerContent>
            <div className={style.panelContent}>
              <div className={style.listSection}>
                <ActivityFeedList
                  items={items}
                  loading={searchResult.loading ?? false}
                  loadingMore={
                    (searchResult.loading ?? false) && items.length > 0
                  }
                  hasMore={hasMore}
                  onLoadMore={handleLoadMore}
                  error={searchResult.error}
                />
              </div>
              {commentsHref && (
                <div className={style.inputSection}>
                  <CommentInput
                    commentsHref={commentsHref}
                    onSuccess={handleCommentSuccess}
                    placeholder="Add a comment..."
                  />
                </div>
              )}
            </div>
          </DrawerContent>
        </>
      )}
    </Drawer>
  );
};

interface ActivityFeedPanelOpenButtonProps {
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  tooltipPosition?: string;
  tooltipAlignment?: 'start' | 'end' | 'center';
}

export const ActivityFeedPanelOpenButton = ({
  buttonSize = 'md',
  tooltipPosition = 'top',
  tooltipAlignment = 'center',
}: ActivityFeedPanelOpenButtonProps) => {
  const setOpen = useSetAtom(isActivityFeedPanelOpenAtom);

  return (
    <Button
      hasIconOnly
      kind="tertiary"
      size={buttonSize}
      renderIcon={AddComment}
      iconDescription="Activity"
      tooltipAlignment={tooltipAlignment}
      tooltipPosition={tooltipPosition}
      onClick={() => setOpen(true)}
      className={style.openButton}
    />
  );
};
